import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { SidebarInset, SidebarProvider } from '@savvy-kit/templates/sidebar';
import { isNil } from 'es-toolkit';
import { useAsPath } from 'src/hooks/use-as-path';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

import type { ClientPortalCrumb } from './types';
import { AdvisorImpersonationBar } from '../../../../components/dashboard/dashboard-advisor-impersonation-bar';
import { ClientPortalBackLink } from './client-portal-back-link';
import { ClientPortalBreadcrumbs } from './client-portal-breadcrumbs';
import { ClientPortalMobileTabBar } from './client-portal-mobile-tab-bar';
import { ClientPortalMobileTopBar } from './client-portal-mobile-top-bar';
import { ClientPortalSidebar } from './client-portal-sidebar';
import { ClientPortalSurfaceBoundary } from './client-portal-surface-boundary';
import { CLIENT_PORTAL_IMPERSONATION_OFFSET_PX } from './constants';
import { useClientPortalAccountMenu } from './use-client-portal-account-menu';
import { useClientPortalFirmBranding } from './use-client-portal-firm-branding';
import { useClientPortalNav } from './use-client-portal-nav';
import { useClientPortalOpenTasks } from './use-client-portal-open-tasks';
import { useClientPortalSidebarCollapse } from './use-client-portal-sidebar-collapse';
import {
  buildClientPortalCrumbs,
  clientDisplayName,
  clientPortalBackCrumb,
  isPresentString,
  matchClientPortalNavId,
} from './utils';

type ClientPortalShellProps = {
  readonly children?: ReactNode;
  readonly headerTitle?: string;
  readonly leafCrumb?: string;
  readonly parentCrumb?: ClientPortalCrumb;
  readonly headerRightContent?: ReactNode;
  readonly hideMobileBack?: boolean;
  readonly hideHeaderTitle?: boolean;
};

export function ClientPortalShell({
  children,
  headerTitle,
  leafCrumb,
  parentCrumb,
  headerRightContent,
  hideMobileBack,
  hideHeaderTitle,
}: ClientPortalShellProps) {
  const { user } = useAuth();
  const asPath = useAsPath();
  const pathname = usePathname() ?? '';
  const { firmName, firmLogoUrl, loading: brandingLoading } = useClientPortalFirmBranding();
  const { open, setOpen } = useClientPortalSidebarCollapse();
  const nav = useClientPortalNav();
  const openTasks = useClientPortalOpenTasks();
  const accountMenu = useClientPortalAccountMenu();

  const isAdvisorLoggedInAsClient = typeof window !== 'undefined' && !isNil(loggedInAsAdvisorID());
  const clientName = clientDisplayName(user?.firstName, user?.lastName);
  const crumbs = buildClientPortalCrumbs({ path: asPath, leaf: leafCrumb ?? headerTitle, parent: parentCrumb });
  const activeNavId = matchClientPortalNavId(asPath);
  const backCrumb = clientPortalBackCrumb({
    path: asPath,
    personalPortfolioId: nav.personalPortfolioId,
    leaf: leafCrumb,
    parent: parentCrumb,
  });

  const retryNav = useCallback(() => {
    nav.retry();
    openTasks.retry();
  }, [nav.retry, openTasks.retry]);

  return (
    <>
      {isAdvisorLoggedInAsClient && (
        <div className="print:hidden">
          <AdvisorImpersonationBar />
        </div>
      )}
      <SidebarProvider
        open={open}
        onOpenChange={setOpen}
        topOffset={isAdvisorLoggedInAsClient ? CLIENT_PORTAL_IMPERSONATION_OFFSET_PX : 0}
        className={cn(
          'client-portal-shell h-svh min-h-0 overflow-hidden bg-vanilla print:h-auto print:overflow-visible',
          isAdvisorLoggedInAsClient && 'mt-16 h-[calc(100svh-4rem)]',
        )}
      >
        {/* The shell itself never animates in: on first load the splash dissolves onto it, and on navigation it is
            the one thing that has not changed, so any motion here read as the sidebar reloading. */}
        <div className="hidden md:contents print:hidden">
          <ClientPortalSidebar
            activeNavId={activeNavId}
            firmName={firmName}
            firmLogoUrl={firmLogoUrl}
            brandingLoading={brandingLoading}
            clientName={clientName}
            clientEmail={user?.email ?? null}
            clientAvatarUrl={user?.profilePicUrl ?? null}
            nav={nav}
            openTaskCount={openTasks.openCount}
            navFailed={nav.failed || openTasks.failed}
            onRetry={retryNav}
            accountMenu={accountMenu}
          />
        </div>
        <SidebarInset className="min-h-0 min-w-0 gap-0 bg-transparent p-0 md:gap-2 md:p-2">
          <ClientPortalMobileTopBar firmName={firmName} firmLogoUrl={firmLogoUrl} brandingLoading={brandingLoading} />
          <div className="hidden h-8 shrink-0 items-center px-3 md:flex print:hidden">
            <ClientPortalBreadcrumbs crumbs={crumbs} />
          </div>
          {!isNil(backCrumb) && hideMobileBack !== true && (
            <div className="flex shrink-0 items-center bg-card px-4 pt-3 md:hidden print:hidden">
              <ClientPortalBackLink crumb={backCrumb} />
            </div>
          )}
          <div className="flex-1 overflow-auto border-border-light bg-card md:rounded-xl md:border print:overflow-visible print:rounded-none print:border-0">
            {isPresentString(headerTitle) && hideHeaderTitle !== true && (
              <div className="flex items-start justify-between gap-4 px-4 pt-6 md:px-10 md:pt-8">
                <Text variant="h1">{headerTitle}</Text>
                {!isNil(headerRightContent) && <div className="pt-3">{headerRightContent}</div>}
              </div>
            )}
            <ClientPortalSurfaceBoundary pathname={pathname}>{children}</ClientPortalSurfaceBoundary>
          </div>
          <ClientPortalMobileTabBar
            activeNavId={activeNavId}
            clientName={clientName}
            clientEmail={user?.email ?? null}
            clientAvatarUrl={user?.profilePicUrl ?? null}
            nav={nav}
            openTaskCount={openTasks.openCount}
            accountMenu={accountMenu}
          />
        </SidebarInset>
      </SidebarProvider>
    </>
  );
}
